// libs
import { NetworkStatus } from '@apollo/client'
import React, { useCallback, useEffect, useRef, useState } from 'react'

// interfaces
import { IUserProfileType } from 'src/interfaces/user'

// constants
import { CLASS_PREFIX } from 'src/constants'

// components
import GroupList from 'src/components/groups/group_overview/group_list/group_list'
import GroupEmptyItem from 'src/components/groups/group_overview/group_list_items/group_empty_item/group_empty_item'
import GroupRecommendation from 'src/components/groups/group_overview/group_recommendation/group_recommendation'
import ItemGroupHeader from 'src/components/item_group_header/item_group_header'
import LoadingSpinner from 'src/components/loading_spinner/loading_spinner'

// helpers
import { hasEnoughToShow } from 'src/actions/app-state/utils'
import { useScrollListener } from 'src/hooks_shared/use_event_listener'
import { textResources } from 'src/lang/de'
import { isFullProfile } from 'src/utils/profile'

// graphql
import {
  GroupsQueryVariables, useGlobalPermissions,
  useGroups
} from '@lopo-tech/lopo-api'

import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'
import CreateItem from '../../post/create/create_item/create_item'
import './group_overview.scss'

const cls = `${CLASS_PREFIX}group-overview`
const labels = textResources.groups

interface RecommendBtnProps {
  showModal: () => void;
  profileType: IUserProfileType;
}

const GroupRecommendationButton: React.FC<RecommendBtnProps> = ({ showModal, profileType }) => (
  <div className={`${cls}__create-group`}>
    <CreateItem active={!isFullProfile(profileType)} onClick={showModal} label={labels.recommendationTitle} />
  </div>
)

const GroupsOverview: React.FC = () => {
  const [showRecommendModal, setShowRecommendModal] = useState(false)
  const locationShape = useGetAppLocationShape()

  const locationShapesId = locationShape?.id ?? ''
  const variables: GroupsQueryVariables = { locationShapes: locationShapesId }

  const { data, loading, networkStatus, confirmedJoinedGroups, nonConfirmedJoinedGroups, nonJoinedGroups, fetchMore } = useGroups({
    variables,
    notifyOnNetworkStatusChange: true
  })

  const { canRequestGroup } = useGlobalPermissions()

  const currentProfileType = (
    (data?.currentProfile)?.__typename ?? 'IntermediateProfile'
  ) as IUserProfileType

  const showModal = () => setShowRecommendModal(true)
  const hideModal = () => setShowRecommendModal(false)

  // fetchMore guard
  const savedIsFetchingMore = useRef(false)
  useEffect(() => {
    savedIsFetchingMore.current = networkStatus === NetworkStatus.fetchMore
  }, [networkStatus])

  // pagination (pageInfo.nextPage)
  const pageInfo = (data)?.groups?.pageInfo
  const hasMore = Boolean(pageInfo?.nextPage != null)

  const getNextFetchMoreVars = useCallback(() => {
    if (pageInfo?.nextPage != null && locationShapesId) {
      return { locationShapes: locationShapesId, page: pageInfo.nextPage }
    }
    return null
  }, [pageInfo?.nextPage, locationShapesId])

  const listener = useCallback(() => {
    if (!hasMore) return
    if (savedIsFetchingMore.current) return
    if (hasEnoughToShow()) return

    const nextVars = getNextFetchMoreVars()
    if (!nextVars) return

    savedIsFetchingMore.current = true

    fetchMore({
      variables: nextVars,
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev

        const prevItems = (prev)?.groups?.items ?? []
        const newItems = (fetchMoreResult)?.groups?.items ?? []

        const mergedItems = [...prevItems]
        for (const g of newItems) {
          if (!mergedItems.some((x) => x.id === g.id)) {
            mergedItems.push(g)
          }
        }

        return {
          ...prev,
          groups: {
            ...(prev).groups,
            ...(fetchMoreResult).groups,
            items: mergedItems
          }
        }
      }
    }).finally(() => {
      savedIsFetchingMore.current = false
    })
  }, [fetchMore, getNextFetchMoreVars, hasMore])

  useScrollListener(listener, { passive: true })

  // loading gates
  if (!loading && !data) {
    return (
      <div className={cls}>
        <ItemGroupHeader title={labels.moreGroups} />
        <GroupEmptyItem />
      </div>
    )
  }

  const isInitialLoading = loading && !data
  if (isInitialLoading) {
    return <LoadingSpinner shown={true} />
  }

  return (
    <div className={cls}>
      {canRequestGroup && <GroupRecommendationButton showModal={showModal} profileType={currentProfileType} />}

      {confirmedJoinedGroups.length > 0 && (
        <GroupList
          groups={confirmedJoinedGroups}
          isLoading={loading}
          label={labels.myGroups}
          displayLastPostAt={true}
        />
      )}

      {nonConfirmedJoinedGroups.length > 0 && (
        <GroupList
          groups={nonConfirmedJoinedGroups}
          isLoading={loading}
          label={labels.pendingGroups}
          displayLastPostAt={false}
        />
      )}

      <GroupList
        groups={nonJoinedGroups}
        isLoading={loading}
        label={nonJoinedGroups.length > 0 ? labels.moreGroups : undefined}
        displayLastPostAt={false}
      />

      {showRecommendModal && <GroupRecommendation hideModal={hideModal} />}
    </div>
  )
}

export default GroupsOverview
